"use client";

/**
 * 全局顶栏（2026-10-07 从 (dashboard)/layout.tsx 提升）。
 *
 * ## 为什么必须提升到 [locale]/layout.tsx
 *
 * 原先这排导航只挂在 (dashboard) 路由组 —— 只有项目列表页有。
 * 用户从项目列表点进 素材库/设置/项目编辑 后，顶栏整个消失，
 * 想去别的页面必须先浏览器后退 —— 用户反馈「这些功能应该一直保持在
 * 所有页面顶部随时可以切换」。导航是全局概念，挂载层必须也是全局层。
 *
 * ## 为什么是 client 组件
 *
 * 「随时切换」的可用性核心是**知道自己在哪**：当前路由对应的图标要高亮，
 * 否则 6 个一样的灰图标分不清入口。高亮需要 usePathname()，只能 client。
 *
 * ## 链接为什么全部带 locale 前缀（修掉历史不一致）
 *
 * 原实现里 /settings、/settings/prompts 不带前缀（当时的注释说「既有不一致，
 * 不跟随」）。挂在 dashboard 页时问题被掩盖（next-intl middleware 会重定向
 * 到默认语言，zh 用户无感）；全局化后用户可能从 ja/ko 会话点进去，
 * 无前缀链接会把人踢回 zh —— 带上前缀，语言跟着会话走。
 */
import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  Bot,
  BookOpen,
  FolderOpen,
  House,
  Images,
  Palette,
  Settings,
  Wand2,
  Workflow,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/language-switcher";
import { LogoIcon } from "@/components/logo";
import { routing } from "@/i18n/routing";
import { recordVisit } from "@/lib/home/use-home-prefs";

export function GlobalHeader() {
  const t = useTranslations("common");
  const ta = useTranslations("assets");
  const pathname = usePathname() || "/";

  // locale 取自路径第一段（middleware 保证存在）；防御性回退到默认语言
  const seg = pathname.split("/")[1];
  const locale = routing.locales.includes(seg as (typeof routing.locales)[number])
    ? seg
    : routing.defaultLocale;

  const base = `/${locale}`;

  /**
   * 🔴 访问埋点挂在这里（2026-10-08 首页「常用功能」的数据源）。
   *
   * 为什么在顶栏而不是各页面：埋点分散到 100+ 路由里必然漏挂，
   * 而漏挂是**静默**的 —— 首页只会显得「没人用」，查不出原因。
   * 顶栏是全站唯一常驻组件，挂一处即覆盖全部路由。
   * 换路由才写（依赖 pathname），且跳过首页自身（否则「访问最多」永远是首页）。
   */
  useVisitTracking(pathname);

  /**
   * active 判定。
   * 🔴 「项目」必须特殊处理：它的 href 是 `${base}`（= /zh），
   * 而**所有**路由都以 /zh 开头 —— 直接 startsWith 会让它永远高亮。
   * 正确语义：项目列表本身 + 一切项目编辑页（/zh/project/...）都算「项目」。
   * settings 与 prompts 是包含关系，prompts 必须先判（否则点进提示词管理，
   * 「设置」和「提示词」同时亮）。
   */
  const nav = [
    {
      href: `${base}/home`,
      label: t("navHome"),
      icon: House,
      active: pathname === `${base}/home`,
    },
    {
      href: base,
      label: t("navProjects"),
      icon: FolderOpen,
      active:
        pathname === base ||
        // 🔴 首页必须先排除，否则 `/zh/home` 会同时点亮「首页」和「项目」两个图标。
        // 与 settings/prompts 的包含关系同理：更具体的路径先判。
        (pathname.startsWith(`${base}/project`) && !pathname.startsWith(`${base}/home`)),
    },
    {
      // 工作台：一级入口 → 域内二级 tab（工作台生图/画库/灵感），
      // 不拆成三个一级入口。active 用 startsWith 覆盖全部子 tab。
      href: `${base}/studio`,
      label: t("navStudio"),
      icon: Palette,
      active: pathname.startsWith(`${base}/studio`),
    },
    {
      href: `${base}/pipeline`,
      label: t("pipeline"),
      icon: Workflow,
      active: pathname.startsWith(`${base}/pipeline`),
    },
    {
      // agent 控制台：与流程编排相邻 —— 一个是「谁在执行」，一个是「执行到哪」。
      href: `${base}/agent`,
      label: t("navAgent"),
      icon: Bot,
      active: pathname.startsWith(`${base}/agent`),
    },
    {
      href: `${base}/assets`,
      label: ta("title"),
      icon: Images,
      active: pathname.startsWith(`${base}/assets`),
    },
    {
      href: `${base}/skills`,
      label: t("skills"),
      icon: BookOpen,
      active: pathname.startsWith(`${base}/skills`),
    },
    {
      href: `${base}/settings/prompts`,
      label: t("promptTemplates"),
      icon: Wand2,
      active: pathname.startsWith(`${base}/settings/prompts`),
    },
    {
      href: `${base}/settings`,
      label: t("navSettings"),
      icon: Settings,
      active:
        pathname.startsWith(`${base}/settings`) &&
        !pathname.startsWith(`${base}/settings/prompts`),
    },
  ];

  return (
    <header className="sticky top-0 z-40 flex h-14 flex-shrink-0 items-center justify-between gap-2 overflow-hidden border-b border-[var(--border-subtle)] bg-white/80 px-3 backdrop-blur-xl sm:px-4 lg:px-6">
      {/* 🔴 logo 回首页（不是回项目列表）：首页现在是「进门第一屏」，
          logo 的通用语义是「回起点」。项目列表另有独立图标入口。
          `min-w-0 shrink` + 文字 truncate：窄屏下 appName 比右侧导航簇更
          容易把 header 撑宽，让文字先让位、导航图标后让位。 */}
      <Link href={`${base}/home`} className="group flex min-w-0 shrink items-center gap-2">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
          <LogoIcon size={14} />
        </div>
        <span className="hidden truncate font-display text-sm font-semibold text-[var(--text-primary)] sm:inline">
          {t("appName")}
        </span>
      </Link>
      {/* `min-w-0` + 允许收缩：图标是 h-8 w-8 定宽，加 overflow-hidden 后
          390px 下不再产生横向滚动。tabIndex 保键盘可达。 */}
      <div className="flex min-w-0 flex-shrink items-center gap-0.5 sm:gap-1">
        {nav.map(({ href, label, icon: Icon, active }) => (
          <Link
            key={href}
            href={href}
            title={label}
            aria-label={label}
            aria-current={active ? "page" : undefined}
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
              active
                ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                : "text-[var(--text-muted)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Icon className="h-4 w-4" />
          </Link>
        ))}
        <LanguageSwitcher />
      </div>
    </header>
  );
}

/**
 * 路由访问埋点（2026-10-08）。
 *
 * 抽成本文件内的独立 hook 而不是内联 useEffect：顶栏已经因导航数组而长，
 * 再塞一个 effect 会让「导航定义」和「副作用」混在同一段代码里，
 * 后来人改导航时容易顺手把 effect 一起挪走或复制一份。
 */
function useVisitTracking(pathname: string) {
  useEffect(() => {
    recordVisit(pathname);
  }, [pathname]);
}
